import React, { createContext, useState, useContext, useEffect } from "react";
import { useAuth } from "./AuthContext";
import { organizationAPI, branchAPI } from "../services/api";
import storage from "../utils/storage";

const OrganizationBranchContext = createContext();

export const OrganizationBranchProvider = ({ children }) => {
  const { user } = useAuth();
  const [organizations, setOrganizations] = useState([]);
  const [currentOrganization, setCurrentOrganization] = useState(null);
  const [branches, setBranches] = useState([]);
  const [currentBranch, setCurrentBranch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [organizationMode, setOrganizationMode] = useState("single"); // "single" or "multi-branch"

  // Load user's organizations and determine mode
  const loadOrganizations = async () => {
    try {
      setLoading(true);
      console.log("🔄 Loading organizations...");
      const response = await organizationAPI.getAll();
      const userOrganizations = response.data.data;
      console.log("✅ Loaded organizations:", userOrganizations);
      setOrganizations(userOrganizations);

      // Determine the mode based on organization count and branches
      if (userOrganizations.length === 1) {
        const org = userOrganizations[0];
        console.log("🏢 Single organization mode, org:", org);
        setCurrentOrganization(org);

        // Check if this organization has branches
        const branchResponse = await branchAPI.getByOrganization(org._id);
        const orgBranches = branchResponse.data.data || [];
        console.log("🌿 Loaded branches for single org:", orgBranches);
        setBranches(orgBranches);

        if (orgBranches.length === 0) {
          // Single organization, no branches - treat as simple company
          console.log("📝 Single org, no branches - setting single mode");
          setOrganizationMode("single");
          setCurrentBranch(null);
        } else {
          // Single organization with branches - enable branch switching
          console.log("📝 Single org with branches - setting multi-branch mode");
          setOrganizationMode("multi-branch");

          // Set default branch (headquarters if exists, otherwise first branch)
          const defaultBranch =
            orgBranches.find((b) => b.branchType === "headquarters") ||
            orgBranches[0];
          setCurrentBranch(defaultBranch);

          // Save current branch to localStorage
          localStorage.setItem("currentBranch", JSON.stringify(defaultBranch));
        }
      } else {
        // Multiple organizations - let user choose
        console.log("🏢 Multiple organizations mode");
        setOrganizationMode("multi-organization");
        // Load from localStorage if exists
        const savedOrg = storage.getJSON("currentOrganization");
        const savedBranch = storage.getJSON("currentBranch");

        if (savedOrg) {
          setCurrentOrganization(savedOrg);
          await loadBranchesForOrganization(savedOrg._id);

          if (savedBranch) {
            setCurrentBranch(savedBranch);
          }
        }
      }
    } catch (error) {
      console.error("❌ Error loading organizations:", error);
    } finally {
      setLoading(false);
    }
  };

  // Load branches for specific organization
  const loadBranchesForOrganization = async (organizationId) => {
    try {
      console.log("🔄 Loading branches for organization:", organizationId);
      const response = await branchAPI.getByOrganization(organizationId);
      const orgBranches = response.data.data || [];
      console.log("✅ Loaded branches:", orgBranches);
      setBranches(orgBranches);
      return orgBranches;
    } catch (error) {
      console.error("❌ Error loading branches:", error);
      setBranches([]);
      return [];
    }
  };

  // Switch to different organization
  const switchOrganization = async (organization) => {
    setCurrentOrganization(organization);
    localStorage.setItem("currentOrganization", JSON.stringify(organization));

    // Load branches for this organization
    const orgBranches = await loadBranchesForOrganization(organization._id);

    if (orgBranches.length === 0) {
      setOrganizationMode("single");
      setCurrentBranch(null);
      localStorage.removeItem("currentBranch");
    } else {
      setOrganizationMode("multi-branch");
      const defaultBranch =
        orgBranches.find((b) => b.branchType === "headquarters") ||
        orgBranches[0];
      setCurrentBranch(defaultBranch);
      localStorage.setItem("currentBranch", JSON.stringify(defaultBranch));
    }
  };

  // Switch to different branch
  const switchBranch = (branch) => {
    setCurrentBranch(branch);
    if (branch) {
      localStorage.setItem("currentBranch", JSON.stringify(branch));
    } else {
      localStorage.removeItem("currentBranch");
    }
  };

  // Get current context info
  const getCurrentContext = () => {
    if (organizationMode === "single") {
      return {
        type: "organization",
        name: currentOrganization?.name || "Company",
        id: currentOrganization?._id,
        showBranchSwitcher: false,
      };
    } else if (organizationMode === "multi-branch") {
      return {
        type: "branch",
        name: currentBranch?.name || "Main Branch",
        organizationName: currentOrganization?.name,
        id: currentBranch?._id,
        organizationId: currentOrganization?._id,
        showBranchSwitcher: true,
      };
    } else {
      return {
        type: "multi-organization",
        name: currentOrganization?.name || "Select Organization",
        id: currentOrganization?._id,
        showBranchSwitcher: branches.length > 0,
      };
    }
  };

  // Check if current user is superuser
  const isSuperUser = () => {
    return (
      user?.email === "superuser@admin.com" ||
      user?.role === "super_admin" ||
      user?.role === "superuser" ||
      user?.permissions?.includes("super_admin")
    );
  };

  // Check if user has permission to manage organizations/branches
  const canManageOrganizations = () => {
    // Any authenticated user can manage their own organizations
    return !!user;
  };

  const canManageBranches = () => {
    // Any authenticated user can manage branches in their own organizations
    return !!user;
  };

  const canSwitchOrganizations = () => {
    // Superuser has access to everything
    if (isSuperUser()) return true;

    return (
      organizationMode === "multi-organization" ||
      user?.role === "super_admin" ||
      user?.permissions?.includes("switch_organizations")
    );
  };

  const canSwitchBranches = () => {
    // Superuser has access to everything
    if (isSuperUser()) return true;

    return (
      organizationMode === "multi-branch" ||
      organizationMode === "multi-organization" ||
      user?.role === "super_admin" ||
      user?.role === "organization_admin" ||
      user?.permissions?.includes("switch_branches")
    );
  };

  // Check if user can view all data (superuser privilege)
  const canViewAllData = () => {
    return isSuperUser();
  };

  // Get organization display name with auto-naming logic
  const getOrganizationDisplayName = (org, branch = null) => {
    if (!org) return "Unknown Organization";

    // If organization name is blank → Auto-fill as "<branchName> Organization" or "Default Organization"
    if (!org.name || org.name.trim() === "") {
      if (branch && branch.name && branch.name.trim() !== "") {
        return `${branch.name} Organization`;
      }
      return "Default Organization";
    }

    return org.name;
  };

  // Get branch display name with auto-naming logic
  const getBranchDisplayName = (branch) => {
    if (!branch) return "Unknown Branch";

    // If branchName is blank → Auto-fill as "Main Branch" or "Head Office" (based on branch type)
    if (!branch.name || branch.name.trim() === "") {
      if (branch.branchType === "headquarters") {
        return "Head Office";
      }
      return "Main Branch";
    }

    return branch.name;
  };

  // Get data scope text for current context
  const getDataScopeText = () => {
    if (organizationMode === "single") {
      return `All ${getOrganizationDisplayName(currentOrganization)} Data`;
    } else if (organizationMode === "multi-branch") {
      return currentBranch
        ? `${getBranchDisplayName(currentBranch)} Branch Only`
        : "All Branches";
    } else {
      return currentOrganization
        ? `${getOrganizationDisplayName(currentOrganization)} Organization`
        : "All Organizations";
    }
  };

  // Initialize on component mount
  useEffect(() => {
    if (user) {
      loadOrganizations();
    }
  }, [user]);

  // Refresh data when branches are added/removed
  const refreshBranches = async () => {
    console.log("🔄 Refreshing branches...");
    if (currentOrganization) {
      console.log("🔄 Refreshing branches for organization:", currentOrganization._id);
      await loadBranchesForOrganization(currentOrganization._id);
    } else {
      console.log("⚠️ No current organization to refresh branches for");
    }
  };

  const refreshOrganizations = async () => {
    await loadOrganizations();
  };

  const value = {
    // State
    organizations,
    currentOrganization,
    branches,
    currentBranch,
    organizationMode,
    loading,

    // Actions
    switchOrganization,
    switchBranch,
    loadBranchesForOrganization,
    refreshBranches,
    refreshOrganizations,

    // Helpers
    getCurrentContext,
    canManageOrganizations,
    canManageBranches,
    canSwitchOrganizations,
    canSwitchBranches,
    canViewAllData,
    getOrganizationDisplayName,
    getBranchDisplayName,
    getDataScopeText,
  };

  return (
    <OrganizationBranchContext.Provider value={value}>
      {children}
    </OrganizationBranchContext.Provider>
  );
};

export const useOrganizationBranch = () => {
  const context = useContext(OrganizationBranchContext);
  if (!context) {
    throw new Error(
      "useOrganizationBranch must be used within an OrganizationBranchProvider"
    );
  }
  return context;
};
